import type { TFunction } from 'i18next';
import type React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  type AccessEntry,
  type AccessFilters,
  type CurrentUser,
  accessLog as loadAccessLog,
  markNoteReviewed,
  type SessionFilters,
  type SessionHistory,
  sendReportEmail,
  sessionDetail,
  sessionHistory,
  type UnansweredAlert,
  unansweredAlerts,
  usageReport,
} from '../api.ts';
import { formatDuration, formatWhen } from '../format.ts';
import { useAnnounce } from '../hooks/useAnnounce.tsx';
import { useCsvExport } from '../hooks/useCsvExport.ts';
import { useLoad } from '../hooks/useLoad.ts';
import { usePdfExport } from '../hooks/usePdfExport.ts';
import { errorMessage } from '../messages.ts';

/**
 * Amministrazione dell'ente (mock 3: `c-hist`, `c-session`, `c-report`,
 * `c-audit`).
 *
 * Quattro schermate del mock e **una sola voce di navigazione**: la console si
 * apre dal telefono e i volontari hanno in media 45-65 anni (sezione 11).
 * Nove linguette in cima a una barra sono nove bersagli da 30 pixel; queste
 * tre sezioni rispondono alla stessa domanda — "cosa ha fatto questo ente, e
 * chi l'ha guardato" — e stanno insieme (`docs/DECISIONI.md`, AG8).
 *
 * Nessuna di queste schermate scrive niente, con una sola eccezione dichiarata:
 * "segna come rivista in supervisione", che è l'unica modifica che un
 * debriefing ammette (AC3) e che fino a ieri era una rotta senza schermata.
 *
 * Il **fornitore in assistenza** vede le stesse schermate in sola lettura
 * (sezione 9bis): `canWrite` arriva dal server e la pagina non inventa
 * permessi.
 *
 * Le tre viste hanno adesso **la stessa testa**: titolo, quanti elementi ci
 * sono, i filtri con la loro azione staccata, il contenuto, e in fondo un
 * `details` con le frasi che spiegano. Prima ognuna ne aveva una sua — una con
 * due paragrafi di prosa prima del primo numero, una con l'esportazione in
 * mezzo ai filtri — e cambiare linguetta voleva dire ricominciare a cercare
 * dove fossero le cose.
 */

type Props = { user: CurrentUser; headingRef?: React.RefObject<HTMLHeadingElement | null> };

type Section = 'alerts' | 'history' | 'report' | 'audit';

/**
 * `alerts` per prima.
 *
 * "L'avviso più importante" (mock 4, sezione 9quinquies) è l'unica delle
 * quattro sezioni che parla di persone che non hanno ricevuto risposta,
 * adesso: le altre tre restano cose da consultare. La scheda attiva
 * all'apertura resta comunque `history` (sotto), quindi questo ordine da
 * solo non basta a far vedere l'avviso a chi non apre "Rendicontazione" —
 * è un limite noto, non un difetto ignorato (`docs/DECISIONI.md`, AZ2).
 */
const SECTIONS: Section[] = ['alerts', 'history', 'report', 'audit'];

export function AdminPage({ user, headingRef }: Props) {
  const { t } = useTranslation();
  const [section, setSection] = useState<Section>('history');

  /**
   * Il titolo della sezione riceve il fuoco al cambio.
   *
   * Come per la navigazione principale: sostituire tutto il contenuto lasciando
   * il fuoco dov'era vuol dire che chi usa uno screen reader non sa che la
   * schermata è cambiata (WCAG 2.4.3). Lo sposta il gestore del clic, un frame
   * dopo — quando il titolo della sezione nuova esiste.
   */
  const sectionHeadingRef = useRef<HTMLHeadingElement>(null);

  return (
    <main className="page" id="main" tabIndex={-1}>
      {/*
        Il titolo, e **niente** sotto.

        C'era una descrizione di centoventisei caratteri fra il titolo e la
        prima cosa utile: la barra aveva già detto "Rendicontazione", il titolo
        lo ripeteva, e la riga sotto spiegava a piena larghezza una schermata
        che si spiega da sola appena si vedono i numeri. Ciò che quella riga
        diceva davvero — il contenuto delle conversazioni non esiste — è
        rimasto, dentro il `details` dello storico, che è il punto in cui
        qualcuno può chiederselo.
      */}
      <h2 ref={headingRef} tabIndex={-1}>
        {t('admin.title')}
      </h2>

      <nav aria-label={t('admin.sections_label')} className="subnav">
        {SECTIONS.map((entry) => (
          <button
            key={entry}
            type="button"
            className={section === entry ? 'tab is-on' : 'tab'}
            aria-current={section === entry ? 'true' : undefined}
            onClick={() => {
              setSection(entry);
              // Un frame dopo: il titolo della sezione nuova non esiste ancora.
              globalThis.requestAnimationFrame?.(() => sectionHeadingRef.current?.focus());
            }}
          >
            {t(`admin.section_${entry}`)}
          </button>
        ))}
      </nav>

      {section === 'alerts' ? <AlertsSection user={user} headingRef={sectionHeadingRef} /> : null}
      {section === 'history' ? <HistorySection user={user} headingRef={sectionHeadingRef} /> : null}
      {section === 'report' ? <ReportSection headingRef={sectionHeadingRef} /> : null}
      {section === 'audit' ? <AuditSection user={user} headingRef={sectionHeadingRef} /> : null}
    </main>
  );
}

// ------------------------------------------------------------------ avvisi

/**
 * L'avviso per le chiamate senza risposta, dalla parte del coordinatore
 * (sezione 9quinquies).
 *
 * "Significa che l'ente ha turni scoperti e che persone reali non hanno
 * ricevuto risposta. Va segnalato al coordinatore, non archiviato." Fino a
 * ieri esisteva solo nella console del fornitore; questa sezione è l'unica
 * cosa nuova di questa fase, ed è per questo che sta **prima** delle altre
 * tre — non perché sia più consultata, ma perché è l'unica che parla di
 * persone che non hanno ricevuto risposta *adesso*, non di uno storico.
 *
 * Sola lettura, per il coordinatore come per il fornitore in assistenza: chi
 * chiude l'avviso resta il fornitore, dalla propria console
 * (`docs/DECISIONI.md`, AZ2). Qui non c'è nessun pulsante "gestisci".
 */
function AlertsSection({
  user,
  headingRef,
}: {
  user: CurrentUser;
  headingRef: React.RefObject<HTMLHeadingElement | null>;
}) {
  const { t, i18n } = useTranslation();
  const { say, announcer } = useAnnounce();

  const load = useCallback((signal: AbortSignal) => unansweredAlerts({ signal }), []);
  const { data, failed } = useLoad(load, {
    onLoaded: (next) => say(t('admin.results', { count: next.alerts.length }), 'quiet'),
    onError: (error) => say(`${t('admin.load_failed')} ${errorMessage(t, error)}`, 'danger'),
  });
  const alerts = data?.alerts;

  const when = (iso: string): string =>
    formatWhen(iso, i18n.resolvedLanguage, user.tenant?.timezone);

  return (
    <section className="card" aria-labelledby="alerts-heading">
      <div className="section-head">
        <h3 id="alerts-heading" ref={headingRef} tabIndex={-1}>
          {t('admin.alerts_title')}
        </h3>
        {alerts ? (
          <p className="count">{t('admin.count_alerts', { count: alerts.length })}</p>
        ) : null}
      </div>

      {announcer}

      {!alerts && !failed ? <Loading t={t} /> : null}
      {alerts && alerts.length === 0 ? (
        <div className="empty">
          <p>{t('admin.alerts_empty')}</p>
        </div>
      ) : null}

      <ul className="plain-list">
        {alerts?.map((alert) => (
          <li
            key={alert.id}
            /*
              Aperto vuol dire "guarda qui adesso": è la stessa evidenza che
              `PlatformPage` dà agli avvisi urgenti, e qui vale per tutti,
              perché in questa lista non ce n'è un altro tipo.
            */
            className={alert.handledAt ? 'row' : 'row row--flagged'}
          >
            <div className="stack stack--tight">
              <p className="row__title">{describeUnanswered(t, alert)}</p>
              <p className="row__meta">{when(alert.createdAt)}</p>
              <p className="note">
                {alert.handledAt
                  ? t('admin.alert_closed', { when: when(alert.handledAt) })
                  : t('admin.alert_open_note')}
              </p>
            </div>
          </li>
        ))}
      </ul>

      <details className="more">
        <summary>{t('admin.about_alerts')}</summary>
        <p className="muted">{t('admin.alerts_hint')}</p>
        <p className="muted">{t('admin.alerts_readonly_note')}</p>
      </details>
    </section>
  );
}

function describeUnanswered(t: TFunction, alert: UnansweredAlert): string {
  if (!alert.detail) return t('admin.alerts_title');
  return t('admin.alert_unanswered_detail', {
    count: alert.detail.sessions,
    hours: alert.detail.windowHours,
  });
}

// ------------------------------------------------------------------ storico

/**
 * Lo storico delle sessioni (mock 3, `c-hist`).
 *
 * "Nessun contenuto di conversazione, solo metadati e note anonime. È ciò che
 * rende il registro difendibile."
 *
 * Nell'elenco il testo del debriefing **non c'è**: si vede che c'è, e si legge
 * aprendo la scheda. È l'unico posto del prodotto in cui può finire qualcosa su
 * una persona (sezione 5.8), e non deve scorrere in una tabella di cento righe
 * lasciata aperta durante una riunione.
 */
function HistorySection({
  user,
  headingRef,
}: {
  user: CurrentUser;
  headingRef: React.RefObject<HTMLHeadingElement | null>;
}) {
  const { t, i18n } = useTranslation();
  const [filters, setFilters] = useState<SessionFilters>({});
  /** Il periodo scelto, in giorni: è il valore dell'opzione, stabile fra i disegni. */
  const [days, setDays] = useState('30');
  const [openId, setOpenId] = useState<string>();
  const { say, announcer } = useAnnounce();

  const loadHistory = useCallback(
    (signal: AbortSignal) => sessionHistory(filters, { signal }),
    [filters],
  );
  const { data: state, reload: load } = useLoad(loadHistory, {
    /**
     * L'elenco è cambiato, e va **detto**.
     *
     * Il fuoco resta correttamente sul filtro appena usato, quindi chi non
     * vede non ha nessun modo di accorgersi che sotto è cambiato tutto — né di
     * sapere quanti risultati ci sono (WCAG 4.1.3, AJ15). Lo dice solo la
     * lettura **corrente**: una risposta tornata in ritardo per un filtro
     * abbandonato annuncerebbe un conteggio che non è più a schermo.
     */
    /*
      `quiet`: il conteggio è già scritto accanto al titolo dell'elenco, e
      ripeterlo in un riquadro sotto voleva dire lo stesso numero due volte a
      due dita di distanza. L'annuncio resta, perché a chi non guarda lo
      schermo quel numero non arriva da nessun'altra parte.
    */
    onLoaded: (next) => say(t('admin.results', { count: next.sessions.length }), 'quiet'),
    onError: (error) => say(`${t('admin.load_failed')} ${errorMessage(t, error)}`, 'danger'),
  });

  const when = (iso: string | null): string =>
    formatWhen(iso, i18n.resolvedLanguage, state?.timezone ?? user.tenant?.timezone);

  const exportCsv = useCsvExport(say);

  /**
   * Chiudendo la scheda, il fuoco torna al titolo dell'elenco.
   *
   * All'andata la scheda lo prende; al ritorno, senza questo, cadeva sul
   * `<body>`: lo screen reader taceva e il Tab ripartiva dall'inizio del
   * documento (WCAG 2.4.3, AJ16).
   */
  const returning = useRef(false);
  useEffect(() => {
    if (openId || !returning.current) return;
    returning.current = false;
    headingRef.current?.focus();
  }, [openId, headingRef]);

  if (openId) {
    return (
      <SessionCard
        id={openId}
        user={user}
        headingRef={headingRef}
        onBack={() => {
          returning.current = true;
          setOpenId(undefined);
          void load();
        }}
      />
    );
  }

  const goTo = (page: number): void => setFilters((current) => ({ ...current, page }));

  return (
    <section className="card" aria-labelledby="history-heading">
      <div className="section-head">
        <h3 id="history-heading" ref={headingRef} tabIndex={-1}>
          {t('admin.history_title')}
        </h3>
        {/*
          Quante righe ci sono davanti, **a chi guarda**.

          Il conteggio esisteva solo come annuncio: chi non usa uno screen
          reader non ha mai saputo se le conversazioni filtrate erano quattro o
          cento, e i comandi di pagina stavano dopo tutte e cento (AQ7).
        */}
        {state ? (
          <p className="count">{t('admin.count_sessions', { count: state.sessions.length })}</p>
        ) : null}
      </div>

      {announcer}

      <div className="filters">
        <div className="field field--inline">
          <label htmlFor="history-channel">{t('admin.filter_channel')}</label>
          <select
            id="history-channel"
            value={filters.channel ?? ''}
            onChange={(event) =>
              setFilters((current) => ({
                ...current,
                channel: event.target.value || undefined,
                page: 0,
              }))
            }
          >
            <option value="">{t('admin.filter_all_channels')}</option>
            <option value="audio">{t('debrief.channel_audio')}</option>
            <option value="video">{t('debrief.channel_video')}</option>
            <option value="chat">{t('debrief.channel_chat')}</option>
          </select>
        </div>

        <div className="field field--inline">
          <label htmlFor="history-risk">{t('admin.filter_risk')}</label>
          <select
            id="history-risk"
            value={filters.risk ?? ''}
            onChange={(event) =>
              setFilters((current) => ({
                ...current,
                risk: event.target.value || undefined,
                page: 0,
              }))
            }
          >
            <option value="">{t('admin.filter_all_risks')}</option>
            <option value="low">{t('debrief.risk_low')}</option>
            <option value="follow_up">{t('debrief.risk_follow_up')}</option>
            <option value="critical">{t('debrief.risk_critical')}</option>
          </select>
        </div>

        <div className="field field--inline">
          <label htmlFor="history-days">{t('admin.filter_period')}</label>
          <select
            id="history-days"
            value={days}
            onChange={(event) => {
              setDays(event.target.value);
              setFilters((current) => ({ ...current, from: daysAgo(event.target.value), page: 0 }));
            }}
          >
            <option value="30">{t('admin.period_30')}</option>
            <option value="90">{t('admin.period_90')}</option>
            <option value="365">{t('admin.period_365')}</option>
          </select>
        </div>

        {/*
          L'esportazione **non è un filtro**, e adesso si vede.

          Stava in fila con le tre tendine, allo stesso peso: l'unica azione
          della sezione era indistinguibile dai comandi che la governano.
        */}
        <div className="filters__actions">
          <button
            type="button"
            className="button"
            onClick={() => void exportCsv(`/api/admin/sessions.csv${queryOf(filters)}`)}
          >
            {t('admin.export_csv')}
          </button>
        </div>
      </div>

      <Pager state={state} onGo={goTo} />

      {!state ? <Loading t={t} /> : null}
      {state && state.sessions.length === 0 ? (
        <div className="empty">
          <p>{t('admin.history_empty')}</p>
        </div>
      ) : null}

      <ul className="plain-list">
        {state?.sessions.map((session) => (
          <li key={session.id} className="row">
            <div>
              <p className="row__title">
                {t('admin.session_line', {
                  when: when(session.requestedAt),
                  channel: t(`debrief.channel_${session.channel}`),
                })}
              </p>
              {/*
                Una riga di contorno, non due paragrafi.

                Durata, chi ha risposto, se era anonima e com'è andato il
                debriefing erano due righe dello stesso peso del titolo: la
                riga aveva tre righe e nessuna era la prima.
              */}
              <p className="row__meta">
                {[
                  t('admin.session_duration', {
                    duration: formatDuration(session.durationSeconds),
                  }),
                  session.volunteer?.displayName
                    ? t(
                        session.volunteer.isFormer
                          ? 'admin.session_volunteer_former'
                          : 'admin.session_volunteer',
                        { name: session.volunteer.displayName },
                      )
                    : t('admin.session_no_volunteer'),
                  session.anonymous
                    ? t('moderation.session_anonymous')
                    : t('moderation.session_registered'),
                  riskLine(session.note, t),
                ].join(' · ')}
              </p>
            </div>
            <div className="row__actions">
              <button
                type="button"
                className="button"
                onClick={() => setOpenId(session.id)}
                /*
                  A schermo una parola, nel nome per esteso **quale**
                  conversazione.

                  Il nome accessibile è quello di prima, identico: in un elenco
                  di cento righe "apri" ripetuto cento volte è indistinguibile
                  per chi le scorre con uno screen reader (WCAG 2.4.6). Ma
                  scritto cento volte a tutta larghezza era anche una colonna
                  di pulsanti larghi mezzo schermo, tutti diversi e tutti
                  uguali: la data è già la prima cosa che la riga dice.
                */
                aria-label={t('admin.open_session', { when: when(session.requestedAt) })}
              >
                {t('admin.open')}
              </button>
            </div>
          </li>
        ))}
      </ul>

      <Pager state={state} onGo={goTo} />

      {/*
        Le frasi che spiegano, dietro una porta che si vede.

        Erano centocinquantatré caratteri fra il titolo e i filtri, cioè prima
        di qualunque cosa si sia venuti a leggere. Restano nell'albero del
        documento — `details` le nasconde alla vista, non a chi cerca nel testo
        — e la prima riga della schermata è tornata a essere il contenuto.
      */}
      <details className="more">
        <summary>{t('admin.about_history')}</summary>
        <p className="muted">{t('admin.description')}</p>
        <p className="muted">{t('admin.history_hint')}</p>
      </details>
    </section>
  );
}

/**
 * La scheda di una conversazione (mock 3, `c-session`).
 *
 * "Metadati e nota del volontario. Nessuna registrazione, nessuna trascrizione,
 * nessun contenuto: non esistono e non si possono recuperare." La frase sta
 * anche nella schermata, e non è una nota legale: è l'unica cosa che spiega
 * perché la nota del volontario conta così tanto.
 */
function SessionCard({
  id,
  user,
  headingRef,
  onBack,
}: {
  id: string;
  user: CurrentUser;
  headingRef: React.RefObject<HTMLHeadingElement | null>;
  onBack: () => void;
}) {
  const { t, i18n } = useTranslation();
  const [busy, setBusy] = useState(false);
  const { say, announcer } = useAnnounce();

  const loadDetail = useCallback((signal: AbortSignal) => sessionDetail(id, { signal }), [id]);
  const { data: detail, reload: load } = useLoad(loadDetail, {
    onError: (error) => say(`${t('admin.load_failed')} ${errorMessage(t, error)}`, 'danger'),
  });

  useEffect(() => {
    headingRef.current?.focus();
  }, [headingRef]);

  const when = (iso: string | null): string =>
    formatWhen(iso, i18n.resolvedLanguage, user.tenant?.timezone);

  const review = async (noteId: string): Promise<void> => {
    if (busy) return;
    setBusy(true);
    try {
      await markNoteReviewed(noteId);
      say(t('moderation.note_reviewed_ok'));
      await load();
    } catch (error) {
      say(`${t('moderation.note_review_failed')} ${errorMessage(t, error)}`, 'danger');
    } finally {
      setBusy(false);
    }
  };

  const session = detail?.session;

  return (
    <section className="card" aria-labelledby="session-heading">
      {/* La stessa testa delle tre viste: il titolo e, accanto, ciò che la governa. */}
      <div className="section-head">
        <h3 id="session-heading" ref={headingRef} tabIndex={-1}>
          {t('admin.session_title')}
        </h3>
        <div className="actions">
          {/* Tornare indietro è servizio: c'è, e non chiama. */}
          <button type="button" className="button button--quiet" onClick={onBack}>
            {t('admin.back_to_history')}
          </button>
        </div>
      </div>

      {announcer}

      {!detail ? <Loading t={t} /> : null}

      {session ? (
        <dl className="facts">
          <div>
            <dt>{t('admin.fact_when')}</dt>
            <dd>{when(session.requestedAt)}</dd>
          </div>
          <div>
            <dt>{t('admin.fact_channel')}</dt>
            <dd>
              {t(`debrief.channel_${session.channel}`)} ·{' '}
              {session.anonymous
                ? t('moderation.session_anonymous')
                : t('moderation.session_registered')}
            </dd>
          </div>
          <div>
            <dt>{t('admin.fact_duration')}</dt>
            <dd>{formatDuration(session.durationSeconds)}</dd>
          </div>
          <div>
            <dt>{t('admin.fact_volunteer')}</dt>
            <dd>
              {session.volunteer?.displayName
                ? session.volunteer.isFormer
                  ? t('admin.volunteer_former', { name: session.volunteer.displayName })
                  : session.volunteer.displayName
                : t('admin.session_no_volunteer')}
            </dd>
          </div>
          <div>
            <dt>{t('admin.fact_outcome')}</dt>
            <dd>{session.outcome ? t(`admin.outcome_${session.outcome}`) : '—'}</dd>
          </div>
          {detail.supervision ? (
            <div>
              <dt>{t('admin.fact_supervision')}</dt>
              <dd>
                {t('admin.supervision_line', {
                  name: detail.supervision.supervisor ?? t('supervision.none_title'),
                  when: when(detail.supervision.startedAt),
                })}
              </dd>
            </div>
          ) : null}
        </dl>
      ) : null}

      {detail ? (
        <div className="note">
          <p className="row__title">{t('admin.debrief_title')}</p>
          {!detail.note ? <p className="muted">{t('moderation.note_none')}</p> : null}
          {detail.note ? (
            <>
              <p className="muted">
                {t('moderation.note_line', {
                  risk: t(
                    detail.note.riskLevel
                      ? `debrief.risk_${detail.note.riskLevel}`
                      : 'debrief.risk_none',
                  ),
                  age: t(
                    detail.note.ageBand ? `debrief.age_${detail.note.ageBand}` : 'debrief.age_none',
                  ),
                  topic: t(
                    detail.note.topic ? `debrief.topic_${detail.note.topic}` : 'debrief.topic_none',
                  ),
                })}
              </p>
              {/*
                La cornice **prima** delle parole di quella sera.

                Chi legge deve sapere chi le ha scritte e in che condizioni
                prima di leggerle, non dopo: sono la nota di una persona che
                usciva da una conversazione difficile, non un verbale.
              */}
              <p className="muted">{t('admin.no_recording_note')}</p>
              {detail.note.text ? <p>{detail.note.text}</p> : null}
              {detail.note.referredExternally ? (
                <p className="muted">{t('moderation.note_referred')}</p>
              ) : null}
              <p className="muted">{t('admin.declared_note')}</p>
              {detail.note.reviewedAt ? (
                <p className="muted">
                  {t('admin.note_reviewed_by', {
                    name: detail.note.reviewedBy ?? t('moderation.unknown_person'),
                    when: when(detail.note.reviewedAt),
                  })}
                </p>
              ) : detail.canWrite ? (
                <button
                  type="button"
                  className="button"
                  disabled={busy}
                  onClick={() => void review(detail.note?.id ?? '')}
                >
                  {t('moderation.note_review')}
                </button>
              ) : (
                /* Il fornitore in assistenza legge e basta (sezione 9bis). */
                <p className="note">{t('admin.read_only')}</p>
              )}
            </>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}

// ------------------------------------------------------------------ consumo

/**
 * Consumo e resoconti (mock 3, `c-report`).
 *
 * "Il resoconto mensile serve alla rendicontazione dei bandi e del 5x1000. È il
 * motivo per cui un ente paga."
 *
 * I numeri vengono **prima** delle frasi che li commentano. Si leggevano
 * duecentottanta caratteri di prosa — due paragrafi consecutivi — prima del
 * primo numero, e altri due sotto: chi apre questa schermata la apre per i
 * minuti, e la spiegazione di come sono calcolati serve dopo averli visti, a
 * chi se lo chiede.
 *
 * Il banner del superamento resta dov'era, cioè **attaccato ai minuti**: dice
 * la cosa che conta più del numero — il servizio continua. Nessuna chiamata
 * viene mai rifiutata o chiusa per i minuti (sezione 9), e chi apre questa
 * schermata dopo aver visto "oltre il piano" deve leggerlo prima di
 * preoccuparsi.
 */
function ReportSection({ headingRef }: { headingRef: React.RefObject<HTMLHeadingElement | null> }) {
  const { t, i18n } = useTranslation();
  const [month, setMonth] = useState<string>('');
  const [sending, setSending] = useState(false);
  const { say, announcer } = useAnnounce();

  const loadReport = useCallback(
    (signal: AbortSignal) =>
      usageReport(month ? { from: month, to: monthAfter(month) } : {}, { signal }),
    [month],
  );
  const { data: report } = useLoad(loadReport, {
    onError: (error) => say(`${t('admin.load_failed')} ${errorMessage(t, error)}`, 'danger'),
  });

  const download = useCsvExport(say);
  const exportCsv = (from?: string): Promise<void> =>
    download(`/api/admin/report.csv${from ? queryOf({ from, to: monthAfter(from) }) : ''}`);

  const downloadPdf = usePdfExport(say);
  const exportPdf = (from?: string): Promise<void> =>
    downloadPdf(`/api/admin/report.pdf${from ? queryOf({ from, to: monthAfter(from) }) : ''}`);

  /**
   * "Invia al coordinatore" (sezione 9): il PDF del periodo scelto, per
   * email. Su richiesta esplicita — questo pulsante — mai un automatismo:
   * il vincolo 3 non riguarda lo staff, ma la logica resta "lo chiede chi
   * riceve".
   */
  const sendToCoordinator = async (): Promise<void> => {
    setSending(true);
    try {
      await sendReportEmail(month ? { from: month, to: monthAfter(month) } : {});
      say(t('admin.report_email_sent'));
    } catch (error) {
      say(`${t('admin.report_email_failed')} ${errorMessage(t, error)}`, 'danger');
    } finally {
      setSending(false);
    }
  };

  const monthLabel = (iso: string): string => {
    try {
      return new Intl.DateTimeFormat(i18n.resolvedLanguage, {
        month: 'long',
        year: 'numeric',
        timeZone: 'UTC',
      }).format(new Date(iso));
    } catch {
      return iso.slice(0, 7);
    }
  };

  return (
    <section className="card" aria-labelledby="report-heading">
      {/*
        Qui il titolo sta da solo: `section-head` esiste per mettergli accanto
        ciò che governa la sezione — il conteggio di un elenco, un ritorno, un
        export — e questa vista non ha un elenco da contare, ha sei numeri. Il
        contenitore vuoto c'era, e si leggeva come un pezzo lasciato a metà
        accanto alle due viste gemelle che la pastiglia ce l'hanno.
      */}
      <h3 id="report-heading" ref={headingRef} tabIndex={-1}>
        {t('admin.report_title')}
      </h3>

      {announcer}

      {!report ? <Loading t={t} /> : null}

      {report ? (
        <>
          <div className="filters">
            <div className="field field--inline">
              <label htmlFor="report-month">{t('admin.filter_month')}</label>
              <select
                id="report-month"
                value={month}
                onChange={(event) => setMonth(event.target.value)}
              >
                <option value="">{t('admin.month_current')}</option>
                {report.months.slice(1).map((entry) => (
                  <option key={entry.from} value={entry.from}>
                    {monthLabel(entry.from)}
                  </option>
                ))}
              </select>
            </div>
            <div className="filters__actions">
              <button
                type="button"
                className="button"
                onClick={() => void exportPdf(month || undefined)}
              >
                {t('admin.download_report_pdf')}
              </button>
              <button
                type="button"
                className="button"
                onClick={() => void exportCsv(month || undefined)}
              >
                {t('admin.export_report_csv')}
              </button>
              {/*
                Solo un coordinatore vero, e solo se il canale email esiste
                (`canSendEmail`): il fornitore in assistenza è in sola
                lettura e non deve poter far partire una comunicazione a
                nome dell'ente (sezione 9bis), e senza Resend configurato il
                pulsante direbbe una promessa che non può mantenere (V15).
              */}
              {report.canSendEmail ? (
                <button
                  type="button"
                  className="button"
                  /*
                   * aria-disabled, non disabled: un pulsante disabilitato
                   * che aveva il focus lo perde verso <body> in quasi ogni
                   * browser, e chi ha appena premuto "invia" da tastiera o
                   * screen reader si ritrova disorientato proprio mentre
                   * arriva l'annuncio dell'esito (WCAG 2.4.3). Resta
                   * cliccabile: la doppia richiesta la evita la guardia
                   * nell'onClick, non la rimozione dal tab order.
                   */
                  aria-disabled={sending}
                  aria-busy={sending}
                  onClick={() => {
                    if (!sending) void sendToCoordinator();
                  }}
                >
                  {sending ? t('admin.sending_report') : t('admin.send_report_to_coordinator')}
                </button>
              ) : null}
            </div>
          </div>

          <dl className="facts">
            <div>
              <dt>{t('admin.kpi_minutes')}</dt>
              <dd>{report.totals.minutes}</dd>
            </div>
            <div>
              <dt>{t('admin.kpi_included')}</dt>
              <dd>{report.plan.includedMinutes}</dd>
            </div>
            <div>
              <dt>{t('admin.kpi_extra')}</dt>
              <dd>{report.totals.extraMinutes}</dd>
            </div>
            <div>
              <dt>{t('admin.kpi_sessions')}</dt>
              <dd>{report.totals.sessions}</dd>
            </div>
            <div>
              <dt>{t('admin.kpi_unanswered')}</dt>
              <dd>{report.totals.unanswered}</dd>
            </div>
            <div>
              <dt>{t('admin.kpi_average')}</dt>
              <dd>{formatDuration(report.totals.averageSeconds)}</dd>
            </div>
          </dl>

          {report.totals.extraMinutes > 0 ? (
            <>
              {/*
                Il superamento. La prima frase è "il servizio continua": è la
                sola cosa che chi legge deve sapere subito (sezione 9).

                E il conguaglio **accanto**, non cento righe più in basso: chi
                vede "Extra: 140" si preoccupa lì, e nel prodotto non esiste
                nessun addebito automatico. Compare solo con un extra da
                spiegare: senza, era una frase che rispondeva a una domanda che
                nessuno si era fatto.
              */}
              <p className="banner banner--warn">
                {t('admin.over_plan', { minutes: report.totals.extraMinutes })}
              </p>
              <div className="note">
                <p>{t('admin.no_payments_note')}</p>
              </div>
            </>
          ) : null}

          <h4>{t('admin.by_channel')}</h4>
          <ul className="plain-list">
            {report.byChannel.map((row) => (
              <li key={row.channel} className="row">
                <div>
                  <p className="row__title">{t(`debrief.channel_${row.channel}`)}</p>
                  <p className="row__meta">
                    {t('admin.channel_line', {
                      sessions: row.sessions,
                      minutes: row.minutes,
                      unanswered: row.unanswered,
                    })}
                  </p>
                </div>
              </li>
            ))}
          </ul>
          {report.byChannel.length === 0 ? (
            <div className="empty">
              <p>{t('admin.report_empty')}</p>
            </div>
          ) : null}

          <h4>{t('admin.recent_months')}</h4>
          <ul className="plain-list">
            {report.months.map((entry) => (
              <li key={entry.from} className="row">
                <div>
                  <p className="row__title">{monthLabel(entry.from)}</p>
                  <p className="row__meta">
                    {t('admin.month_line', { sessions: entry.sessions, minutes: entry.minutes })}
                  </p>
                </div>
                <div className="row__actions">
                  <button
                    type="button"
                    className="button"
                    /* Il mese è già il titolo della riga: a schermo basta il nome del formato (mock 3, `c-report`). */
                    aria-label={t('admin.pdf_month', { month: monthLabel(entry.from) })}
                    onClick={() => void exportPdf(entry.from)}
                  >
                    {t('admin.pdf')}
                  </button>
                  <button
                    type="button"
                    className="button"
                    /* Il mese è già il titolo della riga: a schermo basta il verbo. */
                    aria-label={t('admin.export_month', { month: monthLabel(entry.from) })}
                    onClick={() => void exportCsv(entry.from)}
                  >
                    {t('admin.export')}
                  </button>
                </div>
              </li>
            ))}
          </ul>

          {/*
            "I dati anagrafici sono stimati o auto-riferiti, mai verificati … e
            i report devono dichiararlo" (sezione 5.8). Va detto dove il numero
            si legge — e dopo averlo letto, non prima.
          */}
          <details className="more">
            <summary>{t('admin.about_report')}</summary>
            <p className="muted">{t('admin.report_hint')}</p>
            <p className="muted">{t('admin.declared_note')}</p>
            {report.plan.inNetwork ? <p className="muted">{t('admin.in_network_note')}</p> : null}
          </details>
        </>
      ) : null}
    </section>
  );
}

// ---------------------------------------------------------- registro accessi

/**
 * Il registro accessi (mock 3, `c-audit`).
 *
 * "Chi ha visto cosa, incluso il fornitore. Serve all'ente per rispondere se un
 * utente chiede conto dei propri dati."
 *
 * Dalla fase 6 questo registro si riempie **anche in lettura**: aprire la
 * moderazione e leggere il thread di un collega lasciano una riga. È un
 * miglioramento per l'ente e un problema per la schermata, e la risposta è il
 * raggruppamento: una riga per persona, azione e giorno, con quante volte.
 * Non nasconde niente — dice quanto — e chi deve rispondere a una domanda
 * precisa apre la vista riga per riga (AG3).
 */
function AuditSection({
  user,
  headingRef,
}: {
  user: CurrentUser;
  headingRef: React.RefObject<HTMLHeadingElement | null>;
}) {
  const { t, i18n } = useTranslation();
  const [filters, setFilters] = useState<AccessFilters>({});
  const [days, setDays] = useState('30');
  const { say, announcer } = useAnnounce();

  const loadEntries = useCallback(
    (signal: AbortSignal) => loadAccessLog(filters, { signal }),
    [filters],
  );
  const { data: state } = useLoad(loadEntries, {
    // Come nello storico: il filtro cambia l'elenco, e l'elenco lo dice.
    onLoaded: (next) => say(t('admin.results', { count: next.entries.length }), 'quiet'),
    onError: (error) => say(`${t('admin.load_failed')} ${errorMessage(t, error)}`, 'danger'),
  });

  const when = (iso: string): string =>
    formatWhen(iso, i18n.resolvedLanguage, state?.timezone ?? user.tenant?.timezone);

  const download = useCsvExport(say);
  const exportCsv = (): Promise<void> => download(`/api/admin/access-log.csv${queryOf(filters)}`);

  const goTo = (page: number): void => setFilters((current) => ({ ...current, page }));

  return (
    <section className="card" aria-labelledby="audit-heading">
      <div className="section-head">
        <h3 id="audit-heading" ref={headingRef} tabIndex={-1}>
          {t('admin.audit_title')}
        </h3>
        {state ? (
          <p className="count">{t('admin.count_entries', { count: state.entries.length })}</p>
        ) : null}
      </div>

      {announcer}

      <div className="filters">
        <div className="field field--inline">
          <label htmlFor="audit-category">{t('admin.filter_category')}</label>
          <select
            id="audit-category"
            value={filters.category ?? 'all'}
            onChange={(event) =>
              setFilters((current) => ({ ...current, category: event.target.value, page: 0 }))
            }
          >
            <option value="all">{t('admin.category_all')}</option>
            <option value="reads">{t('admin.category_reads')}</option>
            <option value="decisions">{t('admin.category_decisions')}</option>
            <option value="access">{t('admin.category_access')}</option>
            <option value="provider">{t('admin.category_provider')}</option>
          </select>
        </div>

        <div className="field field--inline">
          <label htmlFor="audit-actor">{t('admin.filter_actor')}</label>
          <select
            id="audit-actor"
            value={filters.actorId ?? ''}
            onChange={(event) =>
              setFilters((current) => ({
                ...current,
                actorId: event.target.value || undefined,
                page: 0,
              }))
            }
          >
            <option value="">{t('admin.actor_all')}</option>
            {state?.actors.map((actor) => (
              <option key={actor.id} value={actor.id}>
                {actor.displayName}
              </option>
            ))}
          </select>
        </div>

        <div className="field field--inline">
          <label htmlFor="audit-days">{t('admin.filter_period')}</label>
          <select
            id="audit-days"
            value={days}
            onChange={(event) => {
              setDays(event.target.value);
              setFilters((current) => ({ ...current, from: daysAgo(event.target.value), page: 0 }));
            }}
          >
            <option value="30">{t('admin.period_30')}</option>
            <option value="90">{t('admin.period_90')}</option>
            <option value="365">{t('admin.period_365')}</option>
            <option value="730">{t('admin.period_730')}</option>
          </select>
        </div>

        {/*
          La spunta è una spunta: casella prima, etichetta accanto.

          Dentro `.field` finiva impilata come una tendina — un'etichetta sopra
          un quadratino — e con il foglio di prima era un riquadro bordato alto
          quarantaquattro pixel che nessuno leggeva come una casella.
        */}
        <div className="field field--inline field--check">
          <input
            id="audit-detailed"
            type="checkbox"
            checked={filters.detailed ?? false}
            onChange={(event) =>
              setFilters((current) => ({
                ...current,
                detailed: event.target.checked || undefined,
                page: 0,
              }))
            }
          />
          <label htmlFor="audit-detailed">{t('admin.detailed_label')}</label>
        </div>

        <div className="filters__actions">
          <button type="button" className="button" onClick={() => void exportCsv()}>
            {t('admin.export_csv')}
          </button>
        </div>
      </div>

      <Pager state={state} onGo={goTo} />

      {!state ? <Loading t={t} /> : null}
      {state && state.entries.length === 0 ? (
        <div className="empty">
          <p>{t('admin.audit_empty')}</p>
        </div>
      ) : null}

      <ul className="plain-list">
        {state?.entries.map((entry) => (
          <li
            key={`${entry.id}-${entry.occurredAt}`}
            /*
              Gli accessi del fornitore sono **evidenziati** e non cancellabili
              (sezione 9bis). Il colore non basta: la riga lo dice anche a
              parole, perché chi non distingue i colori deve poterli trovare
              (WCAG 1.4.1).
            */
            className={entry.actorType === 'provider' ? 'row row--flagged' : 'row'}
          >
            <div>
              <p className="row__title">{describeActor(entry, t)}</p>
              {/*
                L'azione ha un nodo suo, il quando gli sta accanto.

                Unendo le due cose in una stringa sola, "accesso come
                coordinatore" smetteva di essere un testo cercabile per intero:
                diventava l'inizio di "accesso come coordinatore · 4 giu,
                19:00". È il modo in cui un percorso in un browser trova la
                riga dell'ingresso del fornitore, ed è anche il modo in cui la
                trova una persona che cerca nella pagina — che è la ragione per
                cui questo registro esiste (sezione 9bis).
              */}
              <p className="row__meta">
                <span>
                  {t(`admin.action_${entry.action.replaceAll('.', '_')}`, {
                    defaultValue: entry.action,
                  })}
                </span>
                {' · '}
                {describeWhen(entry, t, when)}
              </p>
              {/*
                Il motivo di un accesso del fornitore resta una riga sua: è
                la risposta alla domanda per cui qualcuno apre questo
                registro.

                Per `tenant.created`, quel campo non contiene un perché:
                contiene il piano scelto alla creazione (`superadmin.ts`,
                `reason: body.plan`). Mostrarlo con l'etichetta generica
                "Motivo" lo faceva leggere come "Motivo: base" — un piano
                scambiato per una giustificazione. L'etichetta cambia,
                il valore in colonna resta lo stesso per ogni altro evento.
              */}
              {entry.reason ? (
                <p className="row__meta">
                  {t(
                    entry.action === 'tenant.created'
                      ? 'admin.entry_reason_tenant_created'
                      : 'admin.entry_reason',
                    { reason: entry.reason },
                  )}
                </p>
              ) : null}
            </div>
          </li>
        ))}
      </ul>

      <Pager state={state} onGo={goTo} />

      <details className="more">
        <summary>{t('admin.about_audit')}</summary>
        <p className="muted">{t('admin.audit_hint')}</p>
        <p className="muted">
          {state?.grouped ? t('admin.grouped_hint') : t('admin.detailed_hint')}
        </p>
        <p className="muted">{t('admin.audit_footer')}</p>
      </details>
    </section>
  );
}

// ------------------------------------------------------------------ supporto

/**
 * Sto ancora caricando, e non sto dicendo che non c'è niente.
 *
 * Tre barre grigie al posto delle righe che stanno arrivando, e la parola per
 * chi ascolta: "Caricamento…" era l'unico segnale per tutti, e su una riga
 * `muted` in mezzo alla scheda si confondeva con un elenco vuoto. Il segnaposto
 * è `aria-hidden` — a dirlo a chi non guarda è il testo, una volta sola.
 */
function Loading({ t }: { t: TFunction }) {
  return (
    <>
      <p className="visually-hidden">{t('admin.loading')}</p>
      <div aria-hidden="true">
        <span className="skeleton skeleton--wide" />
        <span className="skeleton skeleton--wide" />
        <span className="skeleton skeleton--half" />
      </div>
    </>
  );
}

/**
 * I comandi di pagina, sopra **e** sotto l'elenco.
 *
 * Una pagina è di cento righe (`apps/api/src/routes/admin-reports.ts`): con i
 * comandi solo in fondo, cambiare pagina voleva dire attraversare cento righe
 * ogni volta — con il dito, con il Tab, con lo screen reader. E non compaiono
 * quando la pagina è una sola: due pulsanti disabilitati sono due bersagli che
 * non fanno niente.
 */
function Pager({
  state,
  onGo,
}: {
  state: { page?: number; hasMore?: boolean } | undefined;
  onGo: (page: number) => void;
}) {
  const { t } = useTranslation();
  const page = state?.page ?? 0;
  if (!state || (page === 0 && !state.hasMore)) return null;

  return (
    <div className="actions actions--end">
      <button
        type="button"
        className="button button--quiet"
        disabled={page === 0}
        onClick={() => onGo(Math.max(0, page - 1))}
      >
        {t('admin.page_previous')}
      </button>
      <button
        type="button"
        className="button button--quiet"
        disabled={!state.hasMore}
        onClick={() => onGo(page + 1)}
      >
        {t('admin.page_next')}
      </button>
    </div>
  );
}

function describeActor(entry: AccessEntry, t: TFunction): string {
  if (entry.actorType === 'provider') return t('admin.actor_provider');
  if (entry.actorType === 'system') return t('admin.actor_system');
  return entry.actor ?? t('moderation.unknown_person');
}

/**
 * Quando, e quante volte.
 *
 * Nella vista raggruppata la riga copre un giorno: dire "otto volte, fra le
 * 18:04 e le 22:31" è la differenza fra un registro leggibile e otto righe
 * identiche da scorrere.
 */
function describeWhen(entry: AccessEntry, t: TFunction, when: (iso: string) => string): string {
  if (entry.occurrences <= 1) return when(entry.occurredAt);
  return t('admin.entry_repeated', {
    count: entry.occurrences,
    first: when(entry.firstAt),
    last: when(entry.occurredAt),
  });
}

function riskLine(note: SessionHistory['sessions'][number]['note'], t: TFunction): string {
  if (!note) return t('admin.no_debrief');
  const parts = [t(note.riskLevel ? `debrief.risk_${note.riskLevel}` : 'debrief.risk_none')];
  if (note.reviewedAt) parts.push(t('admin.note_reviewed_short'));
  if (note.hasText) parts.push(t('admin.note_has_text'));
  return parts.join(' · ');
}

function queryOf(filters: Record<string, string | number | boolean | undefined>): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(filters)) {
    if (value === undefined || value === '' || key === 'page') continue;
    params.set(key, String(value));
  }
  const query = params.toString();
  return query ? `?${query}` : '';
}

/**
 * L'inizio della finestra, dal numero di giorni scelto.
 *
 * Il valore dell'`option` è il **numero di giorni**, non l'istante: un ISO al
 * millisecondo cambia a ogni disegno, e dopo la scelta non coincideva più con
 * nessuna opzione — la tendina si svuotava mentre il filtro restava applicato
 * (`docs/DECISIONI.md`, AJ14).
 */
function daysAgo(days: string): string {
  return new Date(Date.now() - Number(days) * 86_400_000).toISOString();
}

function monthAfter(iso: string): string {
  const start = new Date(iso);
  return new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth() + 1, 1)).toISOString();
}
